/**
 * A row of photos with "take photo" and "from gallery": used for survey rooms, boards and points,
 * devices on the drawing, and fixes of inspection remarks. Photos are reduced to JPEG
 * (photos.ts), stored as project assets, and referenced by id.
 */
import { useState } from 'react';
import type { Translate } from '@elec/i18n';
import type { AssetStore } from './assets.ts';
import { pickPhotos, preparePhoto } from './photos.ts';
import { PhotoThumb, Sheet } from './surveyUi.tsx';
import { Icon } from './ui.tsx';

export function PhotoStrip(props: {
  t: Translate;
  assets: AssetStore;
  photos: readonly string[];
  onChange(photos: string[]): void;
  /** Name of the photos, for the viewer and screen readers. */
  title: string;
  readOnly?: boolean;
  notify(msg: string): void;
  /** Hide the camera button (computers without one still get the file picker). */
  noCamera?: boolean;
  testId?: string;
}) {
  const { t } = props;
  const [big, setBig] = useState<string | null>(null);
  const add = async (camera: boolean) => {
    const files = await pickPhotos(camera);
    if (!files.length) return;
    const ids: string[] = [];
    for (const f of files) {
      try {
        const ph = await preparePhoto(f);
        await props.assets.add(ph.id, ph.bytes, ph.mime, false);
        ids.push(ph.id);
      } catch (e) {
        props.notify((e as Error).message);
      }
    }
    if (ids.length) props.onChange([...props.photos, ...ids]);
  };
  return (
    <>
      <div className="sv-photos" data-testid={props.testId ?? 'sv-photos'}>
        {props.photos.map((id) => (
          <PhotoThumb
            key={id}
            bytes={props.assets.bytes.get(id)}
            alt={props.title}
            onClick={() => setBig(id)}
          />
        ))}
        {!props.readOnly && (
          <>
            {!props.noCamera && (
              <button className="sv-photo-add" onClick={() => void add(true)} data-testid="sv-take-photo">
                <Icon name="camera" size={22} />
                <span>{t('survey.takePhoto')}</span>
              </button>
            )}
            <button className="sv-photo-add" onClick={() => void add(false)} data-testid="sv-gallery">
              <Icon name="image" size={22} />
              <span>{t('survey.fromGallery')}</span>
            </button>
          </>
        )}
      </div>
      {big && (
        <Sheet title={props.title} onClose={() => setBig(null)}>
          <PhotoThumb bytes={props.assets.bytes.get(big)} alt={props.title} large />
          {!props.readOnly && (
            <button
              className="sv-danger sv-block"
              onClick={() => {
                props.onChange(props.photos.filter((p) => p !== big));
                setBig(null);
              }}
            >
              {t('survey.removePhoto')}
            </button>
          )}
        </Sheet>
      )}
    </>
  );
}
